import React, { forwardRef, useRef, useState, useCallback } from 'react';
import cn from 'classnames';
import mergeRefs from 'react-merge-refs';
import { useFocus } from '@alfalab/hooks';
import { Button } from '@alfalab/core-components-button/esm';
import { ButtonList } from './components/button-list/component.js';

var styles = {"component":"plate__component_1s02f","foldable":"plate__foldable_1s02f","focused":"plate__focused_1s02f","inner":"plate__inner_1s02f","rounded":"plate__rounded_1s02f","noBorder":"plate__noBorder_1s02f","shadow":"plate__shadow_1s02f","common":"plate__common_1s02f","negative":"plate__negative_1s02f","positive":"plate__positive_1s02f","attention":"plate__attention_1s02f","custom":"plate__custom_1s02f","rect":"plate__rect_1s02f","isHidden":"plate__isHidden_1s02f","leftAddons":"plate__leftAddons_1s02f","rightAddons":"plate__rightAddons_1s02f","subAddons":"plate__subAddons_1s02f","bold":"plate__bold_1s02f","light":"plate__light_1s02f","content":"plate__content_1s02f","isFolded":"plate__isFolded_1s02f","contentContainer":"plate__contentContainer_1s02f","withoutTitle":"plate__withoutTitle_1s02f","limitWidth":"plate__limitWidth_1s02f","description":"plate__description_1s02f","footer":"plate__footer_1s02f","closer":"plate__closer_1s02f","folder":"plate__folder_1s02f","button":"plate__button_1s02f"};
require('./index.css')

/* eslint-disable complexity */
var Plate = forwardRef(function (_a, ref) {
    var _b, _c, _d, _e, _f;
    var hasCloser = _a.hasCloser, _g = _a.foldable, foldableProp = _g === void 0 ? false : _g, foldedProp = _a.folded, _h = _a.defaultFolded, defaultFolded = _h === void 0 ? true : _h, _j = _a.rounded, rounded = _j === void 0 ? true : _j, _k = _a.limitContentWidth, limitContentWidth = _k === void 0 ? true : _k, leftAddons = _a.leftAddons, subAddons = _a.subAddons, children = _a.children, buttons = _a.buttons, title = _a.title, _l = _a.titleView, titleView = _l === void 0 ? 'bold' : _l, _m = _a.view, view = _m === void 0 ? 'common' : _m, _o = _a.border, border = _o === void 0 ? view !== 'custom' : _o, _p = _a.shadow, shadow = _p === void 0 ? view === 'custom' : _p, className = _a.className, buttonsClassName = _a.buttonsClassName, contentClassName = _a.contentClassName, subAddonsClassName = _a.subAddonsClassName, dataTestId = _a.dataTestId, onClick = _a.onClick, onClose = _a.onClose, onToggle = _a.onToggle;
    var plateRef = useRef(null);
    var contentRef = useRef(null);
    var subAddonsRef = useRef(null);
    var focused = useFocus(plateRef, 'keyboard')[0];
    var _q = useState(false), isHidden = _q[0], setIsHidden = _q[1];
    var _r = useState(defaultFolded), foldedState = _r[0], setFoldedState = _r[1];
    var uncontrolled = foldedProp === undefined;
    var foldable = !!title && !!children && foldableProp;
    var folded = uncontrolled ? foldedState : foldedProp;
    var hasButtons = !!buttons && typeof buttons !== 'boolean';
    var hasContent = children || hasButtons;
    var hasSubAddons = !!subAddons && typeof subAddons !== 'boolean';
    var hasAnyAddons = leftAddons || subAddons || foldable || hasCloser;
    var handleClick = useCallback(function (event) {
        var target = event.target;
        var eventInsideComponent = plateRef.current && plateRef.current.contains(target);
        var eventInsideContent = contentRef.current && contentRef.current.contains(target);
        var eventInsideSubAddons = subAddonsRef.current && subAddonsRef.current.contains(target);
        var clickSimilarKeys = ['Enter', ' '].includes(event.key);
        var shouldChangeIsFolded = eventInsideComponent &&
            !eventInsideContent &&
            !eventInsideSubAddons &&
            (event.type === 'click' || clickSimilarKeys);
        if (foldable && shouldChangeIsFolded) {
            if (uncontrolled) {
                setFoldedState(!foldedState);
            }
            if (onToggle) {
                onToggle(event, { folded: !(uncontrolled ? foldedState : foldedProp) });
            }
        }
        if (onClick) {
            onClick(event);
        }
    }, [foldable, onClick, uncontrolled, onToggle, foldedState, foldedProp]);
    var handleClose = useCallback(function (event) {
        setIsHidden(true);
        if (onClose) {
            onClose(event);
        }
    }, [onClose]);
    return (React.createElement("div", { className: cn((_b = {},
            _b[styles.shadow] = shadow,
            _b[styles.rounded] = rounded,
            _b)) },
        React.createElement("div", { className: cn(styles.component, styles[view], (_c = {},
                _c[styles.foldable] = foldable,
                _c[styles.focused] = focused,
                _c[styles.isHidden] = hasCloser && isHidden,
                _c[styles.isFolded] = foldable && folded,
                _c[styles.rounded] = rounded,
                _c[styles.rect] = !rounded,
                _c[styles.noBorder] = !border,
                _c), className), onClick: handleClick, onKeyDown: handleClick, role: 'alert', ref: mergeRefs([plateRef, ref]), 
            /* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */
            tabIndex: foldable ? 0 : -1, "data-test-id": dataTestId },
            React.createElement("div", { className: styles.inner },
                leftAddons && React.createElement("div", { className: styles.leftAddons }, leftAddons),
                React.createElement("div", { className: cn(styles.contentContainer, contentClassName, (_d = {},
                        _d[styles.withoutTitle] = !title && hasAnyAddons,
                        _d[styles.limitWidth] = limitContentWidth,
                        _d)) },
                    title && React.createElement("div", { className: styles[titleView] }, title),
                    hasContent && (React.createElement("div", { ref: contentRef, className: cn(styles.content, (_e = {},
                            _e[styles.isFolded] = foldable && folded,
                            _e)) },
                        children && (React.createElement("div", { className: styles.description }, children)),
                        hasButtons && (React.createElement("div", { className: styles.footer },
                            React.createElement(ButtonList, { buttons: buttons, containerClassName: buttonsClassName, buttonClassName: cn(styles.button, buttonsClassName) })))))),
                hasSubAddons && (React.createElement("div", { ref: subAddonsRef, className: styles.subAddons },
                    React.createElement(ButtonList, { buttons: subAddons, containerClassName: subAddonsClassName, buttonClassName: styles.button }))),
                foldable && (React.createElement("div", { className: styles.rightAddons },
                    React.createElement("div", { className: cn(styles.folder, (_f = {},
                            _f[styles.isFolded] = folded,
                            _f)) }))),
                hasCloser && !foldable && (React.createElement("div", { className: styles.rightAddons },
                    React.createElement(Button, { className: styles.closer, "aria-label": '\u0437\u0430\u043A\u0440\u044B\u0442\u044C', view: 'ghost', onClick: handleClose })))))));
});
/* eslint-enable complexity */
/**
 * Для отображения в сторибуке
 */
Plate.defaultProps = {
    foldable: false,
    defaultFolded: true,
    view: 'common',
};

export { Plate };
